import {
    FormField,
    Select,
    SelectProps,
} from '@amzn/awsui-components-react-v3';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    selectFeatures,
    selectIsLoaded as selectIsFeaturesLoaded,
} from '../../../../../common/store/slices/featureSlice';
import { checkFeature } from '../../../../../common/utils/featureFlag';
import {
    ActivityLMSType,
    ActivityModality,
    ActivityType,
    ActivityViltType,
} from '../../Common/Common';
import { ActivityData } from '../../../../interfaces/activity';

export const LMS_TYPE_DESCRIPTION = 'The LMS used for the class.';

export type LMSTypeValues =
    | ActivityLMSType.Kiku
    | ActivityLMSType.MyClass
    | ActivityLMSType.CustomerLMS
    | ActivityLMSType.SkillBuilder
    | ActivityLMSType.None;

interface LMSTypeOption extends SelectProps.Option {
    readonly label: LMSTypeValues;
    readonly value: string;
}

const lmsTypeOptions: Array<LMSTypeOption> = [
    {
        label: ActivityLMSType.Kiku,
        value: ActivityLMSType.Kiku,
    },
    {
        label: ActivityLMSType.MyClass,
        value: ActivityLMSType.MyClass,
    },
    {
        label: ActivityLMSType.CustomerLMS,
        value: ActivityLMSType.CustomerLMS,
    },
    {
        label: ActivityLMSType.SkillBuilder,
        value: ActivityLMSType.SkillBuilder,
    },
];

const lmsTypeOptionsLookup = lmsTypeOptions.reduce(
    (acc, opt) => {
        acc[opt.label] = opt;
        return acc;
    },
    {} as {
        [key in LMSTypeValues]: LMSTypeOption;
    },
);

interface SelectLMSTypeProps {
    handleFieldEvent: (values: Partial<ActivityData>) => void;
    formValues: {
        activity_modality: string;
        activity_type?: string;
        lms_type: string;
        program?: string;
        v_ilt_type?: string | null;
        classrooms_arn?: string | null;
        classrooms_status?: string | null;
    };
    errors: {
        activity_modality: string;
        lms_type: string;
        v_ilt_type: string;
        classrooms_arn?: String;
        classrooms_status?: String;
    };
}

const SelectLMSType = ({
    handleFieldEvent,
    formValues,
    errors,
}: SelectLMSTypeProps) => {
    const featureFlags = useSelector(selectFeatures);
    const featuresLoaded = useSelector(selectIsFeaturesLoaded);

    const isVILT = formValues.activity_modality === ActivityModality.vILT;
    const isHybrid = formValues.activity_modality === ActivityModality.Hybrid;
    const isDedicatedCloud = formValues.program === 'Dedicated Cloud';
    const isWebinar = formValues.activity_type === ActivityType.Webinar;

    // Conditionally include None option for Dedicated Cloud
    // Exclude myClass for Webinar activities (not yet supported)
    const availableOptions = useMemo(() => {
        let options = isWebinar
            ? lmsTypeOptions.filter(
                  (opt) => opt.value !== ActivityLMSType.MyClass,
              )
            : lmsTypeOptions;

        if (isDedicatedCloud) {
            options = [
                ...options,
                {
                    label: ActivityLMSType.None,
                    value: ActivityLMSType.None,
                },
            ];
        }
        return options;
    }, [isDedicatedCloud, isWebinar]);

    const availableOptionsLookup = useMemo(() => {
        return availableOptions.reduce(
            (acc, opt) => {
                acc[opt.label] = opt;
                return acc;
            },
            {} as {
                [key in LMSTypeValues]: LMSTypeOption;
            },
        );
    }, [availableOptions]);
    const isViltOrHybrid = isVILT || isHybrid;

    useEffect(() => {
        // Only handle vILT type for vILT or Hybrid modalities
        if (!isViltOrHybrid) return;

        // Preserve "Other" vILT type on mount and when LMS type changes
        if (formValues.v_ilt_type === ActivityViltType.Other) {
            handleFieldEvent({
                v_ilt_type: ActivityViltType.Other,
            });
        } else if (formValues.lms_type) {
            // Set default vILT type if not "Other"
            handleViltTypeChange(formValues.lms_type);
        }
    }, [formValues.lms_type, isViltOrHybrid]);

    const handleCustomerLmsPlatformChange = (lmsType) => {
        // Clear customer_lms_platform when changing away from CustomerLMS
        if (
            formValues.lms_type === ActivityLMSType.CustomerLMS &&
            lmsType !== ActivityLMSType.CustomerLMS
        ) {
            handleFieldEvent({
                customer_lms_platform: null,
            });
        }
    };

    const handleViltTypeChange = (lmsType) => {
        let v_ilt_type = formValues.v_ilt_type || '';

        // Preserve "Other" selection when changing LMS type
        // If current vILT type is "Other", keep it as "Other"
        if (formValues.v_ilt_type === ActivityViltType.Other) {
            v_ilt_type = ActivityViltType.Other;
        } else {
            // Default to WebEx if no vILT type is set or it's not "Other"
            // Include None LMS type to support programs like Dedicated Cloud
            // that need to manage their own vILT meetings
            switch (lmsType) {
                case ActivityLMSType.MyClass:
                case ActivityLMSType.Kiku:
                case ActivityLMSType.SkillBuilder:
                case ActivityLMSType.CustomerLMS:
                case ActivityLMSType.None:
                    v_ilt_type = ActivityViltType.WebEx;
                    break;
                default:
                    break;
            }
        }

        // Only call handleFieldEvent if there's a value to set
        if (v_ilt_type) {
            handleFieldEvent({
                v_ilt_type,
            });
        }
    };
    return (
        <FormField
            label={<span>LMS type</span>}
            errorText={errors?.lms_type}
            data-testid="formField-EditLMSType"
        >
            <Select
                placeholder="Select LMS type"
                options={availableOptions}
                selectedOption={
                    formValues.lms_type
                        ? availableOptionsLookup[
                              formValues.lms_type as LMSTypeValues
                          ]
                        : null
                }
                onChange={(e) => {
                    const newLmsType = e.detail.selectedOption.label;
                    handleFieldEvent({
                        lms_type: newLmsType,
                    });
                    handleCustomerLmsPlatformChange(newLmsType);
                    isViltOrHybrid && handleViltTypeChange(newLmsType);
                }}
                data-testid="EditLMSType"
            />
        </FormField>
    );
};

export default SelectLMSType;
